import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ChevronRight, ChevronsLeftRight } from "lucide-react";
import { cn } from "@/shared/utils/commonUtils";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
} from "@/shared/ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/shared/ui/tooltip";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/shared/ui/collapsible";
import {
  Sidebar as UISidebar,
  SidebarContent,
  SidebarGroup,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  SidebarMenuSub,
  SidebarMenuSubItem,
  SidebarMenuSubButton,
  useSidebar,
} from "@/shared/ui/sidebar";

import _ from "lodash";
import { Features } from "@/features";
import { AppRoute, FeatureModule } from "@/shared/types/routing";
import { Button } from "@/shared/ui/button";

interface NavigationItem {
  label: string;
  icon?: React.ComponentType<{ size?: number | string; className?: string }>;
  path: string;
  children?: NavigationItem[];
}

const buildNavigation = (routes: AppRoute[], parentPath: string = "", untitledLabel: string = "Untitled"): NavigationItem[] => {
  return routes
    .filter((route) => route.meta?.showInSidebar)
    .sort((a, b) => (a.meta?.order || 99) - (b.meta?.order || 99))
    .map((route) => {
      // Resolve full path. Handle leading/trailing slashes mostly by joining.
      // If route.path is absolute, use it. If relative, append to parent.
      let fullPath = route.path;
      if (!route.path.startsWith("/")) {
        const parent = parentPath.endsWith("/") ? parentPath : `${parentPath}/`;
        fullPath = `${parent}${route.path}`;
      }

      return {
        label: route.meta?.label || untitledLabel,
        icon: route.meta?.icon,
        path: fullPath,
        children: route.children ? buildNavigation(route.children, fullPath, untitledLabel) : undefined,
      };
    });
};

const allFeatureRoutes = _.flatMap(Features as FeatureModule[], (feature) => {
  if (!feature) {
    console.warn("Sidebar Debug: Found undefined feature in Features array");
    return [];
  }
  return feature.routes || [];
});

// TODO: Enterprise Scalability - Role Based Access Control (RBAC)
// To implement permissions, modify this function to accept the user object
// and filter routes based on `route.meta.roles`.
// Example:
// const buildNavigation = (routes: AppRoute[], parentPath: string = "", user: User | null): NavigationItem[] => {
//   return routes
//     .filter(route => {
//        if (!route.meta?.showInSidebar) return false;
//        // RBAC Check
//        if (route.meta.roles && user && !route.meta.roles.includes(user.role)) return false;
//        return true;
//     })
//     ...
// }

interface AppSidebarProps {
  className?: string;
}

const AppSidebar: React.FC<AppSidebarProps> = ({ className }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation('common');

  // Memoize navigation to support future dynamic filtering (e.g. Permissions/Roles)
  const navigationItems = React.useMemo(() => {
    return buildNavigation(allFeatureRoutes, "", t('layout.sidebar.untitled'));
  }, [t]);

  const { state, toggleSidebar } = useSidebar();

  const isCollapsed = state === "collapsed";

  const isPathActive = (path: string, exact = false) => {
    const currentPath = location.pathname;
    if (exact) return currentPath === path;
    if (currentPath === path) return true;
    if (currentPath.startsWith(path + "/")) return true;
    return false;
  };



  return (
    <TooltipProvider>
      <UISidebar
        collapsible="icon"
        className={cn(
          "relative transition-[width] duration-300 ease-in-out overflow-visible",
          isCollapsed && "cursor-e-resize",
          className
        )}
        onClick={(e) => {
          // If collapsed and user clicks background (not button/link), expand
          if (isCollapsed) {
            const target = e.target as HTMLElement;
            // Check if click is on a clickable element or its children
            const isInteractive = target.closest('a, button, [role="button"]');
            if (!isInteractive) {
              toggleSidebar();
            }
          }
        }}
      >
        {/* Edge Toggle Button - positioned on the right edge of sidebar */}
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              onClick={(e) => {
                e.stopPropagation();
                toggleSidebar();
              }}
              className="absolute top-9 -end-3 z-50 flex h-6 w-6 items-center justify-center rounded-full bg-white border border-gray-200 text-gray-600 shadow-[0_2px_8px_rgba(0,0,0,0.15)] hover:bg-gray-50 hover:text-gray-900 transition-all duration-200 cursor-pointer"
            >
              <ChevronsLeftRight className="h-3.5 w-3.5" />
              <span className="sr-only">{isCollapsed ? t('layout.sidebar.expand') : t('layout.sidebar.collapse')} Sidebar</span>
            </button>
          </TooltipTrigger>
          <TooltipContent side="right" variant="sidebar">
            {isCollapsed ? t('layout.sidebar.expand') : t('layout.sidebar.collapse')} Sidebar
          </TooltipContent>
        </Tooltip>

        <SidebarContent className="transition-[padding] duration-300 px-1">
          <SidebarGroup>
            <SidebarMenu>
              {navigationItems.map((item) => {
                const Icon = item.icon;
                const isActive = isPathActive(item.path);
                const hasChildren = item.children && item.children.length > 0;
                const hasActiveChild =
                  hasChildren && item.children?.some((child) => location.pathname.startsWith(child.path));

                // Wrapper for the item + divider approach (using border-b for enterprise feel)
                return (
                  <React.Fragment key={item.path}>
                    {!hasChildren ? (
                      <SidebarMenuItem className="mb-px border-b border-white/5 last:border-0 relative">
                        <SidebarMenuButton
                          asChild
                          tooltip={isCollapsed ? t(item.label) : undefined}
                          className={cn(
                            "relative h-12 mb-px justify-start gap-3 p-3 rounded-none transition-all duration-200 outline-none group-data-[state=collapsed]/sidebar-wrapper:justify-center group-data-[state=collapsed]/sidebar-wrapper:gap-0 group-data-[collapsible=icon]:!p-2",
                            isActive
                              ? "bg-white/10 text-white font-medium ps-4"
                              : "text-sidebar-foreground/80 hover:bg-white/5 hover:text-white"
                          )}
                        >
                          <Link to={item.path}>
                            {isActive && (
                              <div className="absolute start-0 top-0 bottom-0 w-[4px] bg-white z-10" />
                            )}
                            {Icon && <Icon className={cn("size-5 shrink-0 transition-all duration-200", isActive ? "text-white opacity-100" : "opacity-70 group-hover:opacity-100")} />}
                            <span className={cn("text-sm truncate transition-all duration-300 opacity-100 group-data-[state=collapsed]/sidebar-wrapper:w-0 group-data-[state=collapsed]/sidebar-wrapper:opacity-0 group-data-[state=collapsed]/sidebar-wrapper:overflow-hidden", isActive ? "font-medium" : "font-normal")}>{t(item.label)}</span>
                          </Link>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    ) : isCollapsed ? (
                      <SidebarMenuItem className="mb-px border-b border-white/5 last:border-0 relative">
                        <DropdownMenu>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <DropdownMenuTrigger asChild>
                                <SidebarMenuButton
                                  tooltip={t(item.label)}
                                  className={cn(
                                    "relative h-12 justify-center gap-0 p-2 rounded-none transition-colors duration-200 outline-none hover:bg-white/10",
                                    isActive
                                      ? "bg-white/10 text-white shadow-none hover:bg-white/20 hover:text-white"
                                      : "text-sidebar-foreground/80 hover:text-white"
                                  )}
                                >
                                  {isActive && (
                                    <div className="absolute start-0 top-0 bottom-0 w-[4px] bg-white z-10" />
                                  )}
                                  {Icon && <Icon className={cn("size-5 shrink-0 transition-all duration-200", isActive ? "text-white opacity-100" : "opacity-70 group-hover:opacity-100")} />}
                                </SidebarMenuButton>
                              </DropdownMenuTrigger>
                            </TooltipTrigger>
                            <TooltipContent side="right" variant="sidebar">{t(item.label)}</TooltipContent>
                          </Tooltip>
                          <DropdownMenuContent
                            side="right"
                            align="start"
                            sideOffset={10}
                            alignOffset={-5}
                            className="w-56 p-2 rounded-xl bg-sidebar border border-sidebar-primary/20 shadow-2xl overflow-visible ring-1 ring-white/5
                              before:content-[''] before:absolute before:-start-1.5 before:top-4 before:h-3 before:w-3 before:rounded-sm
                              before:rotate-45 before:bg-sidebar before:border-s before:border-b before:border-sidebar-primary/20"
                          >
                            <div className="px-2 py-1.5 intro-x mb-1">
                              <h4 className="text-xs font-bold uppercase tracking-widest text-sidebar-primary/80">
                                {t(item.label)}
                              </h4>
                            </div>

                            <div className="flex flex-col ps-3 pt-0 pb-2">
                              {item.children?.map((child, index, array) => {
                                const isChildActive = isPathActive(child.path, true);
                                const isLast = index === array.length - 1;

                                return (
                                  <div
                                    key={child.path}
                                    className="relative flex items-center h-8 animate-in slide-in-from-left-2 fade-in duration-300 fill-mode-backwards"
                                    style={{ animationDelay: `${index * 35}ms` }}
                                  >
                                    <div className="absolute start-0 top-0 bottom-0 w-6 flex items-center justify-center z-0">
                                      <svg
                                        className="w-full h-full overflow-visible pointer-events-none"
                                        viewBox="0 0 24 32"
                                      >
                                        <path
                                          d={cn(
                                            "M 12,0 L 12,8",
                                            index === 0 && "M 12,-10 L 12,0",
                                            !isLast && "M 12,8 L 12,32"
                                          )}
                                          fill="none"
                                          stroke="currentColor"
                                          strokeWidth="1"
                                          className="text-sidebar-primary/30"
                                        />

                                        <path
                                          d="M 12,8 Q 12,16 20,16 L 26,16"
                                          fill="none"
                                          stroke="currentColor"
                                          strokeWidth={isChildActive ? "1.5" : "1"}
                                          className={cn(
                                            "transition-colors duration-300",
                                            isChildActive
                                              ? "text-sidebar-primary"
                                              : "text-sidebar-primary/30"
                                          )}
                                        />
                                      </svg>
                                    </div>

                                    <Button
                                      type="button"
                                      variant="ghost"
                                      className={cn(
                                        "relative z-20 flex flex-1 items-center gap-2 rounded-lg py-1 px-3 ms-8 text-xs font-medium transition-all duration-300 border border-transparent h-auto",
                                        isChildActive
                                          ? "bg-gradient-to-r from-sidebar-primary/20 via-sidebar-primary/10 to-transparent text-sidebar-primary font-bold shadow-sm border-sidebar-primary/10"
                                          : "text-sidebar-foreground/70 hover:bg-sidebar-accent/50 hover:text-sidebar-foreground hover:translate-x-1"
                                      )}
                                      onClick={() => navigate(child.path)}
                                    >
                                      <span className="truncate leading-none tracking-normal">{t(child.label)}</span>
                                    </Button>
                                  </div>
                                );
                              })}
                            </div>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </SidebarMenuItem>
                    ) : (
                      <Collapsible
                        key={item.path}
                        asChild
                        defaultOpen={hasActiveChild}
                        className="group/collapsible"
                      >
                        <SidebarMenuItem className="mb-px border-b border-white/5 last:border-0 relative">
                          <CollapsibleTrigger asChild>
                            <SidebarMenuButton
                              className={cn(
                                "relative h-12 justify-start gap-3 p-3 rounded-none transition-colors duration-200 outline-none group-data-[state=collapsed]/sidebar-wrapper:justify-center group-data-[state=collapsed]/sidebar-wrapper:gap-0 group-data-[collapsible=icon]:!p-2",
                                isActive
                                  ? "bg-white/10 text-white font-medium ps-4"
                                  : "text-sidebar-foreground/80 hover:bg-white/5 hover:text-white"
                              )}
                            >
                              {isActive && !hasChildren && (
                                <div className="absolute start-0 top-0 bottom-0 w-[4px] bg-white z-10" />
                              )}
                              {Icon && <Icon className={cn("size-5 shrink-0 transition-all duration-200", isActive ? "text-white opacity-100" : "opacity-70 group-hover:opacity-100")} />}
                              <span className={cn("text-sm truncate transition-all duration-300 opacity-100 group-data-[state=collapsed]/sidebar-wrapper:w-0 group-data-[state=collapsed]/sidebar-wrapper:opacity-0 group-data-[state=collapsed]/sidebar-wrapper:overflow-hidden", isActive ? "font-medium" : "font-normal")}>{t(item.label)}</span>
                              <ChevronRight className="ms-auto size-4 transition-all duration-200 rtl:-scale-x-100 group-data-[state=open]/collapsible:rotate-90 group-data-[state=collapsed]/sidebar-wrapper:w-0 group-data-[state=collapsed]/sidebar-wrapper:opacity-0" />
                            </SidebarMenuButton>
                          </CollapsibleTrigger>
                          <CollapsibleContent>
                            <SidebarMenuSub>
                              {item.children?.map((child) => (
                                <SidebarMenuSubItem key={child.path}>
                                  <SidebarMenuSubButton
                                    asChild
                                    className={cn(
                                      isPathActive(child.path, true)
                                        ? "bg-sidebar-primary/10 text-sidebar-primary font-medium"
                                        : "text-sidebar-foreground/70 hover:bg-sidebar-primary/5 hover:text-sidebar-primary",
                                    )}
                                  >
                                    <Link to={child.path}>
                                      <span>{t(child.label)}</span>
                                    </Link>
                                  </SidebarMenuSubButton>
                                </SidebarMenuSubItem>
                              ))}
                            </SidebarMenuSub>
                          </CollapsibleContent>
                        </SidebarMenuItem>
                      </Collapsible>
                    )}


                  </React.Fragment>
                );
              })}
            </SidebarMenu>
          </SidebarGroup >
        </SidebarContent >

      </UISidebar >
    </TooltipProvider >
  );
};

export default AppSidebar;